import _ from 'lodash';
import { Integration } from 'common-entities';
import RefData from 'ref-data';
import { assetRefService } from '../../common/Services';
import { getDataURIBase64 as missingIntegrationIcon } from '../../common/components/IconPlaceholder.jsx';
import IntegrationEnums from './integration-enums';

//Returns a bool value if the integration is supported by the IntegrationDesignerApp or not
export const isIntegrationDesignerSupported = _.memoize((integrationTypeRefId) =>
  _.includes(Integration.Enums.designerIntegrationSupport, integrationTypeRefId)
);

export const getAssetRefWithFallback = ({
  width = 250,
  height = 250,
  fallbackFontSize = '72px',
  icon,
  name = 'integration',
}) => {
  if (!!icon && icon.length !== 0) {
    return assetRefService.buildAssetRef('static', 'image', `${width}x${height}`, icon);
  } else {
    const fallbackImage = missingIntegrationIcon(name[0].toUpperCase(), width, height, { fontSize: fallbackFontSize });
    return `asset:generatedsvg:${width}x${height}:${fallbackImage}`;
  }
};

/**
 * Prepares wizard-like integration fields before being placed in the redux store.
 * @param {*} wizardSteps - configuration, grouped by forms to be displayed on a several wizard step or section.
 * @param {*} blueprintData - already saved integrations could have values and they are placed in blueprintData.
 * @returns key-value fields object
 */
export const prepareWizardFields = (wizardSteps, blueprintData, existentWizardFields) => {
  const allFields = existentWizardFields.asMutable();
  wizardSteps.forEach((step) => {
    if (step.fields) {
      step.fields.forEach((field) => {
        // data from saved integration
        if (blueprintData && blueprintData[field.id]) {
          allFields[field.id] = blueprintData[field.id];
          // data from pre-defined config (blueprint or default)
        } else if (field.value) {
          allFields[field.id] = field.value;
          // if empty
        } else {
          allFields[field.id] = field.type === Integration.Enums.integrationFieldTypes.MULTISELECT ? [] : '';
        }
      });
    }
  });
  return allFields;
};

/**
 * Hot replacement of the integration data
 * Used in CM agency accounts
 * @param {*} integration
 * @param {*} shouldOverrideName
 * @returns integration
 */
export const replaceIntegrationGenericFields = (integration, shouldOverrideName) => {
  if (integration.typeRef === RefData.Enum.TYPE.INTEGRATION.CAMPAIGNMONITOR && integration.blueprint.steps) {
    const { productName, accountType } = App.globalContext.account.providerProperties;

    const replaceByline = (byline) => byline.replace(/Campaign Monitor/g, productName);

    if (accountType && accountType === RefData.Enum.TYPE.CAMPAIGN_MONITOR.AGENCY) {
      const { steps } = integration.blueprint;
      steps.forEach((step) => {
        // Step level replacements
        if (step.wizardByline) {
          step.wizardByline = replaceByline(step.wizardByline);
        }
        if (step.wizardHelpText) {
          step.wizardHelpText = step.wizardHelpText.replace(
            'https://help.campaignmonitor.com/api-keys',
            'https://help.createsend.com/admin/s/article/admin-api-keys'
          );
        }
        // Field level replacements
        if (step.fields && step.fields.length) {
          step.fields.forEach((field) => {
            if (field.wizardByline) {
              field.wizardByline = replaceByline(field.wizardByline);
            }
          });
        }
      });
      // Integration name level replacement
      if (shouldOverrideName) {
        integration.name = productName;
      }
    }
  }
  return integration;
};

const campaignMonitorDefaultAgencyIcon = 'c/img/brand/app-switcher-agency.svg';
const campaignMonitorDefaultAgencyAssetRef = assetRefService.buildAssetRef(
  'static',
  'image',
  'auto',
  campaignMonitorDefaultAgencyIcon
);

/**
 * Get genetic asset ref for some kinds of integrations (i.e. Campaign Monitor)
 * It overrides the assetRef received from DB
 * @param {*} integrationTypeRef
 * @param {*} defaultAssetRef
 * @returns
 */
export const getIntegrationGenericAssetRef = (integrationTypeRef, defaultAssetRef) => {
  if (integrationTypeRef === RefData.Enum.TYPE.INTEGRATION.CAMPAIGNMONITOR) {
    const { accountType, productLogo } = App.globalContext.account.providerProperties;
    if (accountType && accountType === RefData.Enum.TYPE.CAMPAIGN_MONITOR.AGENCY) {
      return productLogo
        ? assetRefService.buildAssetRef('weburl', 'image', 'auto', productLogo)
        : campaignMonitorDefaultAgencyAssetRef;
    }
    // Campaign Monitor Direct account still uses a default image
    return defaultAssetRef;
  }
  // Icon for other integrations is not changed
  return defaultAssetRef;
};

/**
 * Get genetic icon for some kinds of integrations (i.e. Campaign Monitor)
 * It overrides the icon received from DB
 * (This function is pretty similar to getIntegrationGenericAssetRef, but it takes an icon and returns an icon.
 * So to not parse assetRef to icon and back, 2 different methods has been created)
 * @param {*} integrationTypeRef
 * @param {*} defaultIconName
 * @returns
 */
export const getIntegrationGenericIcon = (integrationTypeRef, defaultIconName) => {
  const defaultIconPath = `${App.globalContext.assetURL}${defaultIconName}`;
  if (integrationTypeRef === RefData.Enum.TYPE.INTEGRATION.CAMPAIGNMONITOR) {
    const { accountType, productLogo } = App.globalContext.account.providerProperties;
    if (accountType && accountType === RefData.Enum.TYPE.CAMPAIGN_MONITOR.AGENCY) {
      return productLogo ? productLogo : `${App.globalContext.assetURL}${campaignMonitorDefaultAgencyIcon}`;
    }
    // Campaign Monitor Direct account still uses a default image
    return defaultIconPath;
  }
  // Icon for other integrations is not changed
  return defaultIconPath;
};

/**
 * Prepares datatypes for wizard mapping fields
 * @param {*} mapping - array of objects ({ from, to,required })
 * @returns mapping array with a `type` field for each element
 */
export const prepareDataTypesForMapping = (mapping) =>
  mapping.map((field) => {
    const _field = { ...field };

    let dataType = IntegrationEnums.dataTypes.STRING;

    switch (true) {
      // Grow side (has more priority, so override ESP side fields if need)
      case ['entry.brandOptIn', 'entry.groupOptIn', 'entry.thirdPartyOptIn'].includes(field.from):
        dataType = IntegrationEnums.dataTypes.BOOLEAN;
        break;
      // Custom ESP side
      case field.to.endsWith('_date'):
        dataType = IntegrationEnums.dataTypes.DATE;
        break;
      case field.to.endsWith('_datetime'):
        dataType = IntegrationEnums.dataTypes.DATETIME;
        break;
      // When type is already provided and no overrides applied
      case !!field.type:
        dataType = field.type;
        break;
    }

    _field.type = dataType;
    return _field;
  });
